import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { StorageReport, ServerBackupItem } from '@/types/superadmin';

export function useSuperAdminStorageReport(enabled: boolean = true) {
  return useQuery<StorageReport | null>({
    queryKey: ['superadmin', 'reports', 'storage'],
    queryFn: async () => {
      const { data } = await api.get<StorageReport | null>('/superadmin/reports/storage');
      return data;
    },
    enabled,
    staleTime: Infinity, // Ad-hoc only; do not auto-refetch in background
  });
}

export function useRunStorageScan() {
  const queryClient = useQueryClient();

  return useMutation<StorageReport, Error>({
    mutationFn: async () => {
      const { data } = await api.post<StorageReport>('/superadmin/reports/storage/scan');
      return data;
    },
    onSuccess: (newReport) => {
      queryClient.setQueryData(['superadmin', 'reports', 'storage'], newReport);
    },
  });
}

export function useSuperAdminServerBackups(enabled: boolean = true) {
  return useQuery<ServerBackupItem[]>({
    queryKey: ['superadmin', 'reports', 'backups'],
    queryFn: async () => {
      const { data } = await api.get<ServerBackupItem[]>('/superadmin/reports/backups');
      return data || [];
    },
    enabled,
  });
}

export function useRestoreServerBackup() {
  const queryClient = useQueryClient();

  return useMutation<
    { ok: boolean; message: string; result: any },
    Error,
    { propertyId: string; filename: string }
  >({
    mutationFn: async ({ propertyId, filename }) => {
      const { data } = await api.post(
        `/superadmin/reports/backups/${propertyId}/${filename}/restore`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'reports', 'backups'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}
